/**
 * Build a filter predicate from the selections returned by ClientHeader.
 *
 * Every tracker previously turned its selections into a JavaScript *string*
 * and ran it through eval(). Display names were spliced in between quotes, so:
 *
 *   - a name containing an apostrophe (O'Neil) produced a SyntaxError and the
 *     filter stopped working (QA finding TASK-04);
 *   - a crafted name could run arbitrary code in the viewer's browser with
 *     their session, since staff names are user-supplied;
 *   - mixing "Unassigned Editor" with a named editor produced
 *     `a === 'X' || deliverable.editor ? false : true`, which operator
 *     precedence turns into excluding X's work instead of including it.
 *
 * This builds an ordinary function instead. The grouping rules are the same
 * ones the strings encoded: selections under the same heading are OR'd, and
 * headings are AND'd together.
 *
 * @param {Array<{title: string, parentTitle: string, userId?: string}>} selections
 * @param {Object<string, (item: any, selection: object) => boolean>} matchers
 *        One matcher per heading (parentTitle). Selections under a heading
 *        with no matcher are ignored.
 * @returns {((item: any) => boolean) | null} null when nothing is selected,
 *          meaning "show everything".
 */
export function buildFilterPredicate(selections, matchers) {
  const groups = new Map();
  for (const selection of selections || []) {
    const heading = selection?.parentTitle;
    if (!heading || typeof matchers[heading] !== "function") continue;
    if (!groups.has(heading)) groups.set(heading, []);
    groups.get(heading).push(selection);
  }

  if (groups.size === 0) return null;

  return (item) => {
    for (const [heading, chosen] of groups) {
      const match = matchers[heading];
      if (!chosen.some((selection) => match(item, selection))) return false;
    }
    return true;
  };
}

/**
 * Match a person field (populated user, or bare id) against a selection.
 *
 * Prefers the stable user id when the selection carries one, so two staff
 * sharing a first name are no longer indistinguishable. Falls back to the
 * first name for options that do not supply an id.
 */
export function matchesPerson(person, selection) {
  if (!person) return false;
  if (selection?.userId) {
    const id = typeof person === "object" ? person._id : person;
    return id != null && String(id) === String(selection.userId);
  }
  return typeof person === "object" && person.firstName === selection?.title;
}

/** Shared editor matcher used by every deliverable tracker. */
export const UNASSIGNED_EDITOR = "Unassigned Editor";

export function matchesEditor(deliverable, selection) {
  if (selection?.title === UNASSIGNED_EDITOR) return !deliverable?.editor;
  return matchesPerson(deliverable?.editor, selection);
}
